import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { SavedSnippet } from '../../../api/types';
import styled from '@/styles';
import {
  Backdrop,
  FooterRow,
  HeaderRow,
  HeaderTitle,
  IconButton,
  ModalCard,
  PrimaryAction,
  TextLink,
} from '../SnippetModalShared';

const ListArea = styled('div', {
  padding: '20px 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  flex: 1,
});

const EmptyState = styled('p', {
  fontSize: 14,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",
  margin: 0,
});

const Card = styled('div', {
  border: '1px solid #DADADA',
  borderRadius: 8,
  padding: '16px 20px',
  cursor: 'pointer',
  background: '#fff',
  variants: {
    selected: {
      true: { borderColor: '#44A735' },
    },
  },
});

const CardRow = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
});

const Radio = styled('div', {
  width: 18,
  height: 18,
  border: '1.5px solid #DADADA',
  borderRadius: '50%',
  flexShrink: 0,
  marginTop: 2,
  boxSizing: 'border-box',
  variants: {
    selected: {
      true: { borderColor: '#44A735', background: '#44A735' },
    },
  },
});

const CardMain = styled('div', {
  flex: 1,
  minWidth: 0,
});

const CardHeader = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 8,
  marginBottom: 8,
});

const CardTitle = styled('p', {
  fontSize: 14,
  fontWeight: 700,
  color: '#1C1C1C',
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
});

const DeleteButton = styled('button', {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 2,
  flexShrink: 0,
  color: '#979797',
  display: 'flex',
  alignItems: 'center',
});

const CardBody = styled('p', {
  fontSize: 13,
  color: '#1C1C1C',
  margin: '0 0 12px',
  fontFamily: "'Manrope', sans-serif",
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
});

const CardMeta = styled('div', {
  borderTop: '1px solid #EAEAEA',
  paddingTop: 10,
  fontSize: 12,
  color: '#5B5B5B',
  fontFamily: "'Manrope', sans-serif",
});

const BackIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ width: 20, height: 20 }}
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const XIcon = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ width: 16, height: 16 }}
  >
    <line x1="4" y1="4" x2="16" y2="16" />
    <line x1="16" y1="4" x2="4" y2="16" />
  </svg>
);

type BrowseSavedModalProps = {
  isOpen: boolean;
  title: string;
  snippets: SavedSnippet[];
  loading?: boolean;
  applying?: boolean;
  onCancel: () => void;
  onApply: (snippet: SavedSnippet) => void;
  onDelete?: (snippet: SavedSnippet) => void;
};

const BrowseSavedModal = ({
  isOpen,
  title,
  snippets,
  loading = false,
  applying = false,
  onCancel,
  onApply,
  onDelete,
}: BrowseSavedModalProps) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    if (!isOpen) setSelectedId(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const selected =
    snippets.find((snippet) => snippet.id === selectedId) || null;
  const headingTitle = title || 'Browse saved instructions';

  const usedLabel = (count: number) =>
    `used ${count} ${count === 1 ? 'time' : 'times'}`;

  const node = (
    <Backdrop
      role="dialog"
      aria-label={headingTitle}
      aria-modal
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <ModalCard size="browse">
        <HeaderRow layout="back">
          <IconButton type="button" onClick={onCancel} aria-label="Close">
            <BackIcon />
          </IconButton>
          <HeaderTitle size="md">{headingTitle}</HeaderTitle>
        </HeaderRow>

        <ListArea>
          {loading ? (
            <EmptyState>Loading…</EmptyState>
          ) : snippets.length === 0 ? (
            <EmptyState>No saved instructions yet.</EmptyState>
          ) : (
            snippets.map((snippet) => {
              const isSelected = snippet.id === selectedId;
              return (
                <Card
                  key={snippet.id}
                  selected={isSelected}
                  onClick={() => setSelectedId(snippet.id)}
                >
                  <CardRow>
                    <Radio selected={isSelected} />
                    <CardMain>
                      <CardHeader>
                        <CardTitle>{snippet.title}</CardTitle>
                        {onDelete ? (
                          <DeleteButton
                            type="button"
                            aria-label={`Delete ${snippet.title}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              onDelete(snippet);
                            }}
                          >
                            <XIcon />
                          </DeleteButton>
                        ) : null}
                      </CardHeader>
                      <CardBody>{snippet.body}</CardBody>
                      <CardMeta>{usedLabel(snippet.used_count)}</CardMeta>
                    </CardMain>
                  </CardRow>
                </Card>
              );
            })
          )}
        </ListArea>

        <FooterRow>
          <TextLink type="button" onClick={onCancel} disabled={applying}>
            Cancel
          </TextLink>
          <PrimaryAction
            type="button"
            disabled={!selected || applying}
            onClick={() => selected && onApply(selected)}
          >
            {applying ? 'Applying…' : 'Apply selected'}
          </PrimaryAction>
        </FooterRow>
      </ModalCard>
    </Backdrop>
  );

  if (typeof document === 'undefined') return node;
  return createPortal(node, document.body);
};

export default BrowseSavedModal;
